import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  Button,
  Col,
  DateField,
  NumberField,
  TextField,
} from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
  DialogMain,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { FacilitySelectField } from './FacilitySelectField';

import { AccountSelect } from '~/modules/transportation/components/@shared/AccountSelect';
import { useCreateDetentionMutation } from '~/modules/transportation/hooks/mutations/useCreateDetentionMutation';
import { useDeleteDetentionMutation } from '~/modules/transportation/hooks/mutations/useDeleteDetentionMutation';
import { useUpdateDetentionMutation } from '~/modules/transportation/hooks/mutations/useUpdateDetentionMutation';
import {
  $detentionForm,
  $resetDetentionForm,
  $updateDetentionForm,
} from '~/modules/transportation/state/detentionForm.localState';
import { DetentionRecord } from '~/modules/transportation/types';

type DetentionDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  detention?: DetentionRecord;
};

export const DetentionDialog = ({
  isOpen,
  onClose,
  onSuccess,
  detention,
}: DetentionDialogProps) => {
  const t = useTranslations();
  const [isEditMode, setIsEditMode] = React.useState(false);
  const form = useAtomValue($detentionForm);
  const resetForm = useSetAtom($resetDetentionForm);
  const updateForm = useSetAtom($updateDetentionForm);
  const toast = useToast();

  const isCreating = !detention;

  const { mutateAsync: createDetention, isPending: isCreatePending } =
    useCreateDetentionMutation();
  const { mutateAsync: updateDetention, isPending: isUpdatePending } =
    useUpdateDetentionMutation();
  const { mutateAsync: deleteDetention, isPending: isDeletePending } =
    useDeleteDetentionMutation();

  const isPending = isCreatePending || isUpdatePending || isDeletePending;

  React.useEffect(() => {
    if (detention) {
      updateForm({
        id: detention.id,
        carrierId: detention.carrierId,
        carrierName: detention.carrierName,
        locationId: detention.locationId,
        locationName: detention.locationName,
        startDate: detention.startDate,
        endDate: detention.endDate,
        freeMinutes: detention.freeMinutes,
        ratePerMinute: String(detention.ratePerMinute),
      });
      setIsEditMode(false);
    } else {
      resetForm();
      setIsEditMode(true);
    }
  }, [detention, updateForm, resetForm]);

  const handleClose = () => {
    onClose();
    setIsEditMode(false);
    resetForm();
  };

  const handleSave = async () => {
    if (!form.carrierId || !form.locationId || !form.startDate) return;

    try {
      if (isCreating) {
        await createDetention({
          carrierId: form.carrierId,
          locationId: form.locationId,
          startDate: form.startDate,
          endDate: form.endDate,
          freeMinutes: form.freeMinutes,
          ratePerMinute: form.ratePerMinute,
        });
        toast.success(t.detentions.messages.createSuccess);
      } else {
        await updateDetention({
          id: detention.id,
          carrierId: form.carrierId,
          locationId: form.locationId,
          startDate: form.startDate,
          endDate: form.endDate,
          freeMinutes: form.freeMinutes,
          ratePerMinute: form.ratePerMinute,
        });
        toast.success(t.detentions.messages.updateSuccess);
      }
      setIsEditMode(false);
      onSuccess();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.detentions.messages.error,
      );
    }
  };

  const handleDelete = async () => {
    if (!detention) return;

    try {
      await deleteDetention({ id: detention.id });
      toast.success(t.detentions.messages.deleteSuccess);
      onSuccess();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.detentions.messages.error,
      );
    }
  };

  const isFormDisabled = !isEditMode && !isCreating;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      isDismissable={false}
      className="@DetentionDialog"
    >
      <DialogContent position="right">
        <DialogHeader slot="title">
          <div className="text-xl font-semibold">
            {isCreating
              ? t.detentions.dialog.title.create
              : isEditMode
                ? t.detentions.dialog.title.edit
                : t.detentions.dialog.title.view}
          </div>
        </DialogHeader>

        <DialogMain>
          <Col gap4>
            <AccountSelect
              label={t.detentions.dialog.fields.carrier}
              value={form.carrierId || null}
              onChange={(account) => {
                updateForm({
                  carrierId: account?.id ?? '',
                  carrierName: account?.name ?? '',
                });
              }}
              isRequired
              isDisabled={isFormDisabled}
            />

            <FacilitySelectField
              label={t.detentions.dialog.fields.location}
              value={form.locationId || null}
              onChange={(facilityId, facilityName) => {
                updateForm({
                  locationId: facilityId ?? '',
                  locationName: facilityName ?? '',
                });
              }}
              isRequired
              isDisabled={isFormDisabled}
            />

            <DateField
              label={t.detentions.dialog.fields.startDate}
              value={form.startDate ?? null}
              onChange={(value: CalendarDate | null) =>
                updateForm({ startDate: value ?? undefined })
              }
              isRequired
              isDisabled={isFormDisabled}
            />

            <DateField
              label={t.detentions.dialog.fields.endDate}
              value={form.endDate ?? null}
              onChange={(value: CalendarDate | null) =>
                updateForm({ endDate: value ?? undefined })
              }
              isDisabled={isFormDisabled}
            />

            <NumberField
              label={t.detentions.dialog.fields.freeMinutes}
              value={form.freeMinutes}
              onChange={(value: number) => updateForm({ freeMinutes: value })}
              isRequired
              isDisabled={isFormDisabled}
              minValue={0}
            />

            <TextField
              label={t.detentions.dialog.fields.ratePerMinute}
              value={form.ratePerMinute}
              onChange={(value: string) => updateForm({ ratePerMinute: value })}
              isRequired
              isDisabled={isFormDisabled}
            />
          </Col>
        </DialogMain>

        <DialogFooter>
          {isCreating ? (
            <>
              <Button variant="outline" onPress={handleClose}>
                {t.detentions.dialog.buttons.cancel}
              </Button>
              <Button
                color="primary"
                variant="solid"
                onPress={handleSave}
                isPending={isPending}
                isLoading={isPending}
              >
                {t.detentions.dialog.buttons.create}
              </Button>
            </>
          ) : isEditMode ? (
            <>
              <Button
                color="failure"
                variant="outline"
                onPress={handleDelete}
                isPending={isDeletePending}
              >
                {t.detentions.dialog.buttons.delete}
              </Button>
              <Button
                variant="outline"
                isPending={isPending}
                onPress={() => {
                  if (detention) {
                    updateForm({
                      carrierId: detention.carrierId,
                      carrierName: detention.carrierName,
                      locationId: detention.locationId,
                      locationName: detention.locationName,
                      startDate: detention.startDate,
                      endDate: detention.endDate,
                      freeMinutes: detention.freeMinutes,
                      ratePerMinute: String(detention.ratePerMinute),
                    });
                  }
                  setIsEditMode(false);
                }}
              >
                {t.detentions.dialog.buttons.cancel}
              </Button>
              <Button
                color="primary"
                variant="solid"
                onPress={handleSave}
                isPending={isPending}
                isLoading={isPending}
              >
                {t.detentions.dialog.buttons.save}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onPress={handleClose}>
                {t.detentions.dialog.buttons.close}
              </Button>
              <Button
                color="primary"
                variant="solid"
                onPress={() => setIsEditMode(true)}
              >
                {t.detentions.dialog.title.edit}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
